import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Quote, Star, Sparkles } from 'lucide-react';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonials = [
    {
      id: 1,
      name: "Cícero Santos",
      role: "Prestador de Serviços",
      text: "Sou novo com o App e também como MEI, não tenho até o momento o que reclamar. Foi muito rápido a abertura do meu CNPJ e tem me ajudado bastante com minhas dúvidas e curiosidades.",
      avatar: "https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741565086/photo-1633332755192-727a05c4013d_1_tc1a0g.jpg",
      rating: 5,
      location: "São Paulo, SP"
    },
    {
      id: 2,
      name: "Expedito Faustino",
      role: "Comerciante",
      text: "Super recomendo! Equipe rápida e bom atendimento. Consegui resolver todas as pendências do meu MEI em poucos dias. O suporte é excepcional.",
      avatar: "https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741564663/Leonardo_Kino_XL_create_an_image_where_an_older_white_man_is_l_0_1_twvkdo.jpg",
      rating: 5,
      location: "Rio de Janeiro, RJ"
    },
    {
      id: 3,
      name: "Rodrigo Santana",
      role: "Autônomo",
      text: "App excelente! Ajuda muito pra quem é MEI. Tem emissão de DAS, lembretes de vencimento, tudo muito prático. Recomendo demais para todos os microempreendedores!",
      avatar: "https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741565086/istockphoto-1171169127-612x612_og5moq.jpg",
      rating: 5,
      location: "Belo Horizonte, MG"
    },
    {
      id: 4,
      name: "Joice Gonçalves",
      role: "Consultora",
      text: "Ótimo app, está atendendo todas as minhas expectativas! Interface intuitiva e funcionalidades completas. Finalmente encontrei uma solução que realmente funciona.",
      avatar: "https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741565089/istockphoto-1386479313-612x612_ufob4o.jpg",
      rating: 5,
      location: "Curitiba, PR"
    },
    {
      id: 5,
      name: "Fernando Lima",
      role: "Freelancer",
      text: "Mudou completamente a forma como gerencio meu MEI. Antes era uma bagunça, agora tenho tudo organizado em um só lugar. Vale muito a pena!",
      avatar: "https://res.cloudinary.com/dlk1ta1fr/image/upload/v1741564660/Leonardo_Kino_XL_create_an_image_where_a_hardworking_black_man_1_piqe0f.jpg",
      rating: 5,
      location: "Salvador, BA"
    }
  ];

  // Auto-play
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const navigate = (newDirection) => {
    setIsAutoPlaying(false);
    setDirection(newDirection);
    
    if (newDirection === 1) {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    } else {
      setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    }

    // Retoma auto-play após 10 segundos
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.9,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1],
      },
    },
    exit: (direction) => ({
      x: direction > 0 ? -300 : 300,
      opacity: 0,
      scale: 0.9,
      transition: {
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1],
      },
    }),
  };

  const getVisibleTestimonials = () => {
    const prev = (currentIndex - 1 + testimonials.length) % testimonials.length;
    const next = (currentIndex + 1) % testimonials.length;
    return { prev, current: currentIndex, next };
  };

  const { prev, current, next } = getVisibleTestimonials();

  return (
    <section className="relative py-24 overflow-hidden bg-gradient-to-b from-gray-50 via-white to-gray-50">
      {/* Background decorativo */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Padrão de aspas gigantes */}
        <div className="absolute top-20 left-10 opacity-[0.03]">
          <Quote size={300} strokeWidth={1} />
        </div>
        <div className="absolute bottom-20 right-10 opacity-[0.03] rotate-180">
          <Quote size={250} strokeWidth={1} />
        </div>

        {/* Círculos gradientes */}
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute -top-20 -right-20 w-96 h-96 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, transparent 70%)',
          }}
        />
        <motion.div
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.15, 0.1, 0.15] }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%)',
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(6, 182, 212, 0.1) 100%)',
              border: '1px solid rgba(16, 185, 129, 0.2)',
            }}
          >
            <Sparkles size={16} className="text-[#10b981]" />
            <span className="text-sm font-medium text-[#10b981]">
              Depoimentos reais
            </span>
          </motion.div>

          <h2 className="text-4xl md:text-5xl font-bold text-[#2C5E50] mb-4">
            O QUE OS MEIs{' '}
            <span className="bg-gradient-to-r from-[#10b981] to-[#06b6d4] bg-clip-text text-transparent">
              FALAM DA GENTE
            </span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Histórias reais de empreendedores que transformaram seus negócios com o MeuMei
          </p>
        </motion.div>

        {/* Carousel de depoimentos */}
        <div className="relative max-w-5xl mx-auto">
          {/* Cards laterais (desktop) */}
          <div className="hidden lg:block">
            {/* Card anterior */}
            <motion.div
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/4 w-72 z-0"
              animate={{ opacity: 0.4, scale: 0.85, x: '-25%' }}
              transition={{ duration: 0.5 }}
            >
              <div className="bg-white rounded-3xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={testimonials[prev].avatar}
                    alt={testimonials[prev].name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{testimonials[prev].name}</p>
                    <p className="text-gray-500 text-xs">{testimonials[prev].role}</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm line-clamp-3">{testimonials[prev].text}</p>
              </div>
            </motion.div>

            {/* Card próximo */}
            <motion.div
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/4 w-72 z-0"
              animate={{ opacity: 0.4, scale: 0.85, x: '25%' }}
              transition={{ duration: 0.5 }}
            >
              <div className="bg-white rounded-3xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={testimonials[next].avatar}
                    alt={testimonials[next].name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{testimonials[next].name}</p>
                    <p className="text-gray-500 text-xs">{testimonials[next].role}</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm line-clamp-3">{testimonials[next].text}</p>
              </div>
            </motion.div>
          </div>

          {/* Card principal */}
          <div className="relative z-10 max-w-2xl mx-auto">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="relative"
              >
                <div 
                  className="bg-white rounded-3xl p-8 md:p-10 relative overflow-hidden"
                  style={{
                    boxShadow: '0 25px 80px rgba(16, 185, 129, 0.15), 0 10px 30px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  {/* Decoração de fundo */}
                  <div 
                    className="absolute top-0 right-0 w-32 h-32 opacity-10"
                    style={{
                      background: 'radial-gradient(circle, #10b981 0%, transparent 70%)',
                    }}
                  />

                  {/* Ícone de aspas */}
                  <motion.div
                    initial={{ scale: 0, rotate: -20 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.2, type: 'spring' }}
                    className="absolute -top-4 -left-4 w-16 h-16 rounded-2xl flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      boxShadow: '0 8px 25px rgba(16, 185, 129, 0.4)',
                    }}
                  >
                    <Quote className="text-white" size={28} />
                  </motion.div>

                  {/* Conteúdo */}
                  <div className="pt-8">
                    {/* Rating */}
                    <div className="flex gap-1 mb-6">
                      {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.1 + i * 0.1 }}
                        >
                          <Star 
                            size={22} 
                            className="text-yellow-400" 
                            fill="#facc15"
                          />
                        </motion.div>
                      ))}
                    </div>

                    {/* Texto do depoimento */}
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                      className="text-gray-700 text-lg md:text-xl leading-relaxed mb-8 italic"
                    >
                      "{testimonials[currentIndex].text}"
                    </motion.p>

                    {/* Autor */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="flex items-center gap-4"
                    >
                      <div className="relative">
                        <img
                          src={testimonials[currentIndex].avatar}
                          alt={testimonials[currentIndex].name}
                          className="w-16 h-16 rounded-full object-cover"
                          style={{
                            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.15)',
                          }}
                        />
                        {/* Badge de verificado */}
                        <div 
                          className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center"
                          style={{
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="white"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </div>
                      </div>

                      <div>
                        <h4 className="font-bold text-[#2C5E50] text-lg">
                          {testimonials[currentIndex].name}
                        </h4>
                        <p className="text-[#10b981] font-medium">
                          {testimonials[currentIndex].role}
                        </p>
                        <p className="text-gray-500 text-sm">
                          {testimonials[currentIndex].location}
                        </p>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Controles de navegação */}
          <div className="flex items-center justify-center gap-6 mt-10">
            {/* Botão anterior */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => navigate(-1)}
              className="w-14 h-14 rounded-full bg-white flex items-center justify-center text-[#2C5E50] hover:text-white hover:bg-[#10b981] transition-all duration-300"
              style={{
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              }}
            >
              <ChevronLeft size={28} />
            </motion.button>

            {/* Indicadores */}
            <div className="flex gap-3">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className="relative group"
                >
                  <motion.div
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      currentIndex === index
                        ? 'bg-[#10b981]'
                        : 'bg-gray-300 group-hover:bg-gray-400'
                    }`}
                    animate={currentIndex === index ? { scale: [1, 1.2, 1] } : {}}
                    transition={{ duration: 0.5 }}
                  />
                  {currentIndex === index && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="absolute inset-0 rounded-full border-2 border-[#10b981]"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1.8, opacity: 0 }}
                      transition={{ duration: 1, repeat: Infinity }}
                    />
                  )}
                </button>
              ))}
            </div>

            {/* Botão próximo */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => navigate(1)}
              className="w-14 h-14 rounded-full bg-white flex items-center justify-center text-[#2C5E50] hover:text-white hover:bg-[#10b981] transition-all duration-300"
              style={{
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              }}
            >
              <ChevronRight size={28} />
            </motion.button>
          </div>

          {/* Progress bar do auto-play */}
          {isAutoPlaying && (
            <div className="max-w-xs mx-auto mt-6">
              <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{
                    background: 'linear-gradient(90deg, #10b981, #06b6d4)',
                  }}
                  initial={{ width: '0%' }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 5, ease: 'linear' }}
                  key={currentIndex}
                />
              </div>
            </div>
          )}
        </div>

        {/* Estatísticas */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto"
        >
          {[
            { number: '2.6M+', label: 'MEIs atendidos' },
            { number: '98%', label: 'Satisfação' },
            { number: '4.8', label: 'Nota média' },
            { number: '24h', label: 'Suporte' },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 * index }}
              className="text-center"
            >
              <div 
                className="text-3xl md:text-4xl font-bold mb-2"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {stat.number}
              </div>
              <div className="text-gray-600 font-medium">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;